import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  IconButton,
  Snackbar,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Slide,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Fab,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Cancel as CancelIcon,
  CheckCircle as CompleteIcon,
  PlayArrow as StartIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelEditIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as DownloadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import { userService } from "../../services/userService";
import { orderService } from "../../services/orderService";
import { useAuth } from "../../contexts/AuthContext";
import authService from "../../services/authService";

// Slide transition for mobile dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Date utility functions
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const dateParts = dateString.split("T")[0].split("-");
    if (dateParts.length === 3) {
      const [year, month, day] = dateParts;
      return `${month}/${day}/${year}`;
    }

    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

const formatDateForInput = (dateString) => {
  if (!dateString) return "";

  try {
    const dateOnly = dateString.split("T")[0];
    return dateOnly;
  } catch (error) {
    console.error("Error formatting date for input:", error);
    return "";
  }
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState({ type: "", status: "" });

  // Assignment states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedMedicalProfessional, setSelectedMedicalProfessional] =
    useState("");
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);
  const [assigning, setAssigning] = useState(false);

  // Enhanced editing states
  const [editingSection, setEditingSection] = useState(null);
  const [editedOrder, setEditedOrder] = useState({});
  const [editedPatient, setEditedPatient] = useState({});
  const [editedPhysician, setEditedPhysician] = useState({});
  const [saving, setSaving] = useState(false);
  const [forceEditMode, setForceEditMode] = useState(false);
  const [editWarningOpen, setEditWarningOpen] = useState(false);

  // Reference data for dropdowns
  const [patients, setPatients] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);

  const [showCustomExamDialog, setShowCustomExamDialog] = useState(false);
  const [newCustomExam, setNewCustomExam] = useState({
    name: "",
    description: "",
    duration: null,
  });

  const [editingExam, setEditingExam] = useState(null);
  const [editExamName, setEditExamName] = useState("");

  // File states
  const [orderFiles, setOrderFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // File preview states
  const [previewDialog, setPreviewDialog] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    fetchOrderDetails();
    fetchReferenceData();
  }, [id]);

  useEffect(() => {
    const fetchMedicalProfessionals = async () => {
      try {
        const response = await userService.getMedicalProfessionals();
        setMedicalProfessionals(response || []);
      } catch (err) {
        console.error("Error fetching medical professionals:", err);
      }
    };

    fetchMedicalProfessionals();
  }, [order]);

  useEffect(() => {
    if (order) {
      fetchOrderFiles();
    }
  }, [order]);

  const fetchOrderFiles = async () => {
    if (!order) return;

    setLoadingFiles(true);
    try {
      const response = await fetch(`/api/v1/orders/${order.id}/files`, {
        headers: {
          Authorization: `Bearer ${authService.getToken()}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const files = await response.json();
        setOrderFiles(files);
      } else {
        console.error("Failed to fetch files");
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFile(true);
    setError("");

    try {
      for (const file of files) {
        // Validate file size
        if (file.size > 50 * 1024 * 1024) {
          throw new Error(`${file.name}: File too large (max 50MB)`);
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("file_type", "document");

        const response = await fetch(`/api/v1/orders/${order.id}/files`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "File upload failed");
        }
      }

      // Refresh files list
      await fetchOrderFiles();
      setSnackbarMessage("File(s) uploaded successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error uploading file:", error);
      setError(`File upload failed: ${error.message}`);
    } finally {
      setUploadingFile(false);
      // Reset file input
      event.target.value = "";
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await fetch(
        `/api/v1/orders/${order.id}/files/${fileId}/download`,
        {
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("File download failed");
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSnackbarMessage("File downloaded successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError("File download failed");
    }
  };

  const handleDeleteFileClick = (file) => {
    setFileToDelete(file);
    setDeleteFileDialogOpen(true);
  };

  const handleDeleteFileConfirm = async () => {
    if (!fileToDelete) return;

    try {
      const response = await fetch(
        `/api/v1/orders/${order.id}/files/${fileToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "File deletion failed");
      }

      // Refresh files list
      await fetchOrderFiles();
      setSnackbarMessage("File deleted successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting file:", error);
      setError(`File deletion failed: ${error.message}`);
    } finally {
      setDeleteFileDialogOpen(false);
      setFileToDelete(null);
    }
  };

  // File preview helper functions
  const canPreviewFile = (mimeType) => {
    if (!mimeType) return false;
    return mimeType.includes("image") || mimeType.includes("pdf");
  };

  const handleViewFile = async (file) => {
    setCurrentFile(file);
    setPreviewError("");
    setZoomLevel(1);

    if (!canPreviewFile(file.mime_type)) {
      // If can't preview, just download
      handleDownloadFile(file.id, file.file_name);
      return;
    }

    setPreviewDialog(true);
    setLoadingPreview(true);

    try {
      const response = await fetch(
        `/api/v1/orders/${order.id}/files/${file.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load file");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setFileContent(url);
    } catch (error) {
      console.error("Error loading file preview:", error);
      setPreviewError("Failed to load file preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewDialog(false);
    setCurrentFile(null);
    if (fileContent) {
      URL.revokeObjectURL(fileContent);
      setFileContent(null);
    }
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <FileIcon />;

    if (mimeType.includes("pdf")) return <PdfIcon color="error" />;
    if (mimeType.includes("image")) return <ImageIcon color="primary" />;
    if (mimeType.includes("word") || mimeType.includes("document"))
      return <DocIcon color="info" />;

    return <FileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  // FIXED: Initialize editing states when order data is available
  useEffect(() => {
    if (order && Object.keys(editedOrder).length === 0) {

      setEditedOrder({
        place_of_service: order.place_of_service || "Home",
        ordering_health_company: order.ordering_health_company || "",
        scheduled_date: order.scheduled_date || "",
        scheduled_time: order.scheduled_time || "",
        scheduled_time_end: order.scheduled_time_end || "",
        special_notes: order.special_notes || "",
        medical_professional_id: order.medical_professional_id || "",

        // FIXED: Proper diagnosis initialization with all required fields
        selected_diagnoses:
          order.diagnoses?.map((d) => {

            const diagnosis = d.diagnosis || d;
            return {
              id: diagnosis.id,
              diagnosis_id: diagnosis.id,
              code: diagnosis.code,
              description: diagnosis.description,
              primary: d.primary || false,
            };
          }) || [],

        // FIXED: Proper exam initialization
        selected_exams:
          order.exams?.map((e) => {
            const examType = e.exam_type || e;
            return {
              id: examType.id,
              name: examType.name,
              description: examType.description,
            };
          }) || [],
      });

      // CRITICAL FIX: Ensure ALL patient fields are initialized, including address_notes
      if (order.patient) {
        const patientData = {
          id: order.patient.id,
          first_name: order.patient.first_name || "",
          last_name: order.patient.last_name || "",
          date_of_birth: order.patient.date_of_birth || "",
          gender: order.patient.gender || "",
          address: order.patient.address || "",
          address_notes: order.patient.address_notes || "", // CRITICAL: Explicitly include
          city: order.patient.city || "",
          state: order.patient.state || "",
          zip_code: order.patient.zip_code || "",
          phone_number: order.patient.phone_number || "",
          email: order.patient.email || "",
        };
        setEditedPatient(patientData);
      }

      if (order.ordering_physician) {
        setEditedPhysician({
          id: order.ordering_physician.id,
          first_name: order.ordering_physician.first_name || "",
          last_name: order.ordering_physician.last_name || "",
          npi: order.ordering_physician.npi || "",
          specialty: order.ordering_physician.specialty || "",
          facility_name: order.ordering_physician.facility_name || "",
          phone_number: order.ordering_physician.phone_number || "",
          email: order.ordering_physician.email || "",
        });
      } else {
        setEditedPhysician({
          first_name: "",
          last_name: "",
          npi: "",
          facility_name: "",
          phone_number: "",
          email: "",
        });
      }
    }
  }, [order, editedOrder]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await orderService.getOrderById(id);

      if (!data) {
        throw new Error("No order data received");
      }

      // FIXED: Better data processing for diagnoses and exams
      const processedOrder = {
        ...data,
        exams: Array.isArray(data.exams) ? data.exams : [],
        diagnoses: Array.isArray(data.diagnoses) ? data.diagnoses : [],
        patient: data.patient || null,
        ordering_physician: data.ordering_physician || null,
        medical_professional: data.medical_professional || null,
      };


      setOrder(processedOrder);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError(err.message || "Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [
        patientsResponse,
        physiciansResponse,
        examTypesResponse,
        diagnosesResponse,
      ] = await Promise.all([
        orderService.getPatients().catch((err) => {
          console.error("Error loading patients:", err);
          return [];
        }),
        orderService.getPhysicians().catch((err) => {
          console.error("Error loading physicians:", err);
          return [];
        }),
        orderService.getExamTypes().catch((err) => {
          console.error("Error loading exam types:", err);
          return [];
        }),
        orderService.getDiagnoses().catch((err) => {
          console.error("Error loading diagnoses:", err);
          return [];
        }),
      ]);

      setPatients(patientsResponse || []);
      setPhysicians(physiciansResponse || []);
      setExamTypes(examTypesResponse || []);
      setDiagnoses(diagnosesResponse || []);

    } catch (err) {
      console.error("Error loading reference data:", err);
    }
  };

  const handleStatusChange = (status) => {
    setDialogAction({ type: "status", status });
    setDialogOpen(true);
  };

  const handleDialogConfirm = async () => {
    setDialogOpen(false);

    if (dialogAction.type === "status") {
      try {
        const response = await orderService.updateOrderStatus(id, {
          status: dialogAction.status,
          notes: `Status updated to ${dialogAction.status}`,
        });

        setOrder(response);
        setSuccess(`Order status updated to ${dialogAction.status}`);
        setTimeout(() => setSuccess(""), 3000);
      } catch (err) {
        console.error("Error updating order status:", err);
        setError(err.response?.data?.detail || "Failed to update order status");
      }
    }
  };

  // ENHANCED: Medical Professional Assignment/Reassignment
  const handleAssignOrder = async () => {
    if (!selectedMedicalProfessional) {
      setError("Please select a medical professional");
      return;
    }

    setAssigning(true);
    setError("");

    try {

      await orderService.assignMedicalProfessional(
        id,
        selectedMedicalProfessional
      );

      // Update status if needed
      if (order.status === "created") {
        await orderService.updateOrderStatus(id, {
          status: "assigned",
          notes: `Order assigned to medical professional`,
        });
      }

      await fetchOrderDetails();

      setSuccess(
        order.medical_professional_id
          ? "Medical professional reassigned successfully"
          : "Order assigned successfully"
      );
      setAssignDialogOpen(false);
      setSelectedMedicalProfessional("");

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error assigning order:", err);
      setError(err.message || "Failed to assign order");
    } finally {
      setAssigning(false);
    }
  };

  // FIXED: Enhanced edit permissions for facility admins
  const canEdit = () => {

    // Super admins can edit everything regardless of status
    if (user?.role === "super_admin") {
      return true;
    }

    // FIXED: Facility admins can edit ALL orders in their facility - NO STATUS RESTRICTIONS
    if (user?.role === "facility_admin") {
      if (user?.facility_id && order?.facility_id === user.facility_id) {
        return true;
      } else {
        return false;
      }
    }

    // Regular admins can edit everything regardless of status
    if (user?.role === "admin") {
      return true;
    }

    // Medical professionals can only edit created and assigned orders
    if (user?.role === "medical_professional") {
      const canEditMP = ["created", "assigned"].includes(order?.status);
      return canEditMP;
    }

    return false;
  };

  const handleEditExamName = async (examId, newName) => {
    try {
      setSaving(true);
      await orderService.updateExamType(examId, { name: newName });

      // Update local state
      setExamTypes((prev) =>
        prev.map((exam) =>
          exam.id === examId ? { ...exam, name: newName } : exam
        )
      );

      setEditingExam(null);
      setSnackbarMessage("Exam name updated successfully");
      setSnackbarOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (section) => {

    if (!canEdit()) {
      setError("You don't have permission to edit this order");
      return;
    }

    setEditingSection(section);
  };

  // FIXED: Enhanced diagnosis selection handler
  const handleDiagnosisEditChange = (event, value) => {

    // Ensure proper data structure for each selected diagnosis
    const formattedDiagnoses = value.map((diagnosis, index) => {
      const formatted = {
        id: diagnosis.id,
        diagnosis_id: diagnosis.id, // Critical for backend compatibility
        code: diagnosis.code,
        description: diagnosis.description,
        primary: index === 0, // First one is always primary
      };
      return formatted;
    });


    setEditedOrder({
      ...editedOrder,
      selected_diagnoses: formattedDiagnoses,
    });
  };

  // FIXED: Enhanced exam selection handler
  const handleExamEditChange = (event, value) => {

    setEditedOrder({
      ...editedOrder,
      selected_exams: value,
    });
  };

  // FIXED: Enhanced save section with proper data transformation
  const handleSaveSection = async () => {
    setSaving(true);
    setError("");

    try {
      let updateData = {};

      if (editingSection === "order") {

        updateData = {
          place_of_service: editedOrder.place_of_service,
          ordering_health_company: editedOrder.ordering_health_company,
          scheduled_date: editedOrder.scheduled_date || null,
          scheduled_time: editedOrder.scheduled_time || null,
          scheduled_time_end: editedOrder.scheduled_time_end || null,
          special_notes: editedOrder.special_notes,
          medical_professional_id: editedOrder.medical_professional_id || null,
        };

        // FIXED: Proper exam transformation with validation
        if (editedOrder.selected_exams) {
          updateData.exams = editedOrder.selected_exams.map((exam) => {
            const examData = {
              exam_type_id: exam.id,
            };
            return examData;
          });
        }

        // FIXED: Proper diagnosis transformation with extensive validation
        if (editedOrder.selected_diagnoses) {

          updateData.diagnoses = editedOrder.selected_diagnoses.map(
            (diagnosis, index) => {

              // Get the diagnosis ID from multiple possible sources
              const diagnosisId = diagnosis.diagnosis_id || diagnosis.id;

              if (!diagnosisId || diagnosisId <= 0) {
                console.error("Invalid diagnosis ID:", diagnosis);
                throw new Error(
                  `Invalid diagnosis ID for: ${diagnosis.code || "unknown"}`
                );
              }

              const processed = {
                diagnosis_id: parseInt(diagnosisId),
                primary: index === 0, // First diagnosis is primary
              };

              return processed;
            }
          );


          // Validate we have at least one valid diagnosis if any were selected
          if (
            updateData.diagnoses.length === 0 &&
            editedOrder.selected_diagnoses.length > 0
          ) {
            throw new Error(
              "No valid diagnoses found. Please check your selections."
            );
          }
        }
      } else if (editingSection === "patient") {
        updateData = { patient_data: editedPatient };
      } else if (editingSection === "physician") {
        updateData = { physician_data: editedPhysician };
      }


      const response = await orderService.updateOrder(id, updateData);

      const processedResponse = {
        ...response,
        exams: Array.isArray(response.exams) ? response.exams : [],
        diagnoses: Array.isArray(response.diagnoses) ? response.diagnoses : [],
        patient: response.patient || null,
        ordering_physician: response.ordering_physician || null,
        medical_professional: response.medical_professional || null,
      };

      setOrder(processedResponse);
      setEditingSection(null);

      setSuccess(
        `${
          editingSection.charAt(0).toUpperCase() + editingSection.slice(1)
        } information updated successfully`
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(`Error updating ${editingSection}:`, err);
      setError(
        err.message ||
          err.response?.data?.detail ||
          `Failed to update ${editingSection}`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);

    // Reset edited data to original order data
    if (order) {
      setEditedOrder({
        place_of_service: order.place_of_service || "Home",
        ordering_health_company: order.ordering_health_company || "",
        scheduled_date: order.scheduled_date || "",
        scheduled_time: order.scheduled_time || "",
        scheduled_time_end: order.scheduled_time_end || "",
        special_notes: order.special_notes || "",
        medical_professional_id: order.medical_professional_id || "",

        selected_diagnoses:
          order.diagnoses?.map((d) => {
            const diagnosis = d.diagnosis || d;
            return {
              id: diagnosis.id,
              diagnosis_id: diagnosis.id,
              code: diagnosis.code,
              description: diagnosis.description,
              primary: d.primary || false,
            };
          }) || [],

        selected_exams:
          order.exams?.map((e) => {
            const examType = e.exam_type || e;
            return {
              id: examType.id,
              name: examType.name,
              description: examType.description,
            };
          }) || [],
      });

      // CRITICAL FIX: Properly reset ALL patient fields including address_notes
      if (order.patient) {
        const restoredPatient = {
          id: order.patient.id,
          first_name: order.patient.first_name || "",
          last_name: order.patient.last_name || "",
          date_of_birth: order.patient.date_of_birth || "",
          gender: order.patient.gender || "",
          address: order.patient.address || "",
          address_notes: order.patient.address_notes || "", // CRITICAL: Explicitly restore
          city: order.patient.city || "",
          state: order.patient.state || "",
          zip_code: order.patient.zip_code || "",
          phone_number: order.patient.phone_number || "",
          email: order.patient.email || "",
        };
        setEditedPatient(restoredPatient);
      }

      if (order.ordering_physician) {
        setEditedPhysician({
          id: order.ordering_physician.id,
          first_name: order.ordering_physician.first_name || "",
          last_name: order.ordering_physician.last_name || "",
          npi: order.ordering_physician.npi || "",
          specialty: order.ordering_physician.specialty || "",
          facility_name: order.ordering_physician.facility_name || "",
          phone_number: order.ordering_physician.phone_number || "",
          email: order.ordering_physician.email || "",
        });
      }
    }
  };

  // ADDED: Handle custom exam creation during editing
  const handleCreateCustomExam = async () => {
    if (!newCustomExam.name?.trim()) {
      setError("Exam name is required");
      return;
    }

    try {
      setSaving(true);
      setError("");


      // Call the API to create the exam type
      const response = await orderService.createExamType({
        name: newCustomExam.name.trim(),
        description: newCustomExam.description?.trim() || "",
        duration: newCustomExam.duration,
      });


      // Create the exam object for local state
      const newExam = {
        id: response.id,
        name: response.name,
        description: response.description || "",
        duration: response.duration,
      };

      // Add to exam types list
      setExamTypes((prev) => [...prev, newExam]);

      // Add to selected exams automatically
      setEditedOrder((prev) => ({
        ...prev,
        selected_exams: [...prev.selected_exams, newExam],
      }));

      // Reset form and hide dialog
      setNewCustomExam({ name: "", description: "", duration: null });
      setShowCustomExamDialog(false);
      setSnackbarMessage(response.message || "Custom exam created and added");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error creating custom exam:", err);
      setError(err.message || "Failed to create custom exam");
    } finally {
      setSaving(false);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      completed: "success",
      in_progress: "primary",
      assigned: "info",
      created: "warning",
      cancelled: "error",
    };
    return (
      <Chip
        label={status?.replace("_", " ") || "unknown"}
        color={statusMap[status] || "default"}
      />
    );
  };

  const getEditButtonTooltip = () => {
    if (canEdit()) return "Edit this section";
    return "You don't have permission to edit this order";
  };

  const shouldShowEditButton = (section) => {
    return canEdit();
  };

  // FIXED: Helper function to safely get diagnosis data
  const getDiagnosisDisplay = (diagnosisItem) => {
    // Handle both direct diagnosis objects and nested diagnosis objects
    const diagnosis = diagnosisItem.diagnosis || diagnosisItem;
    const isPrimary = diagnosisItem.primary || false;

    return {
      id: diagnosis.id,
      code: diagnosis.code || "N/A",
      description: diagnosis.description || "No description",
      isPrimary,
    };
  };

  // FIXED: Helper function to safely get exam data
  const getExamDisplay = (examItem) => {
    // Handle both direct exam objects and nested exam_type objects
    const examType = examItem.exam_type || examItem;
    const notes = examItem.notes || "";

    return {
      id: examType.id,
      name: examType.name || "Unknown Exam",
      description: examType.description || "",
      notes,
    };
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error && !order) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/orders")}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box>
        <Alert severity="warning">Order not found</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/orders")}
          sx={{ mt: 2 }}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 1.5 : 4, bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            component={Link}
            to="/orders"
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Orders
          </Button>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
            Order {order.order_number}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Edit Mode Indicator */}
          {canEdit() && (
            <Chip
              icon={<EditIcon />}
              label="Edit Mode Available"
              color="primary"
              variant="outlined"
            />
          )}

          {/* Action Buttons */}
          {order.status !== "cancelled" && order.status !== "completed" && (
            <>
              {/* FIXED: Only show Cancel and Assign buttons for admin roles */}
              {(user?.role === "super_admin" ||
                user?.role === "admin" ||
                user?.role === "facility_admin") && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => handleStatusChange("cancelled")}
                    sx={{ mr: 1 }}
                  >
                    Cancel Order
                  </Button>

                  {/* Assignment/Reassignment Button */}
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AssignmentIcon />}
                    onClick={() => {
                      setSelectedMedicalProfessional(
                        order.medical_professional_id || ""
                      );
                      setAssignDialogOpen(true);
                    }}
                    disabled={assigning}
                  >
                    {order.medical_professional_id
                      ? "Reassign MP"
                      : "Assign MP"}
                  </Button>
                </>
              )}

              {/* Status update buttons - Available for all roles based on assignment */}
              {order.status === "assigned" &&
                (user?.role === "medical_professional" ? (
                  // Medical professionals can only start orders assigned to them
                  order.medical_professional_id ===
                    user?.medical_professional?.id && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<StartIcon />}
                      onClick={() => handleStatusChange("in_progress")}
                    >
                      Start Order
                    </Button>
                  )
                ) : (
                  // Admin roles can start any order
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<StartIcon />}
                    onClick={() => handleStatusChange("in_progress")}
                  >
                    Start Order
                  </Button>
                ))}

              {order.status === "in_progress" &&
                (user?.role === "medical_professional" ? (
                  // Medical professionals can only complete orders assigned to them
                  order.medical_professional_id ===
                    user?.medical_professional?.id && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CompleteIcon />}
                      onClick={() => handleStatusChange("completed")}
                    >
                      Complete Order
                    </Button>
                  )
                ) : (
                  // Admin roles can complete any order
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CompleteIcon />}
                    onClick={() => handleStatusChange("completed")}
                  >
                    Complete Order
                  </Button>
                ))}
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={isMobile ? 1.5 : 3}>
        <Grid  size={{ xs: 12, md: 8 }}>
          {/* Order Information Section */}
          <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 4, mb: 3, borderRadius: 3, border: "1px solid #E2E8F0" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" color="primary">
                Order Information
              </Typography>
              {shouldShowEditButton("order") && editingSection !== "order" && (
                <Tooltip title={getEditButtonTooltip()}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditClick("order")}
                    size="small"
                    color="primary"
                  >
                    Edit Order
                  </Button>
                </Tooltip>
              )}
              {editingSection === "order" && (
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveSection}
                    disabled={saving}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelEditIcon />}
                    onClick={handleCancelEdit}
                    size="small"
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={isMobile ? 1.5 : 3}>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Order Number
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {order.order_number}
                </Typography>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Box gutterBottom>{getStatusChip(order.status)}</Box>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(order.created_at).toLocaleString()}
                </Typography>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Place of Service
                </Typography>
                {editingSection === "order" ? (
                  <FormControl fullWidth size="small">
                    <Select
                      value={editedOrder.place_of_service || ""}
                      onChange={(e) =>
                        setEditedOrder({
                          ...editedOrder,
                          place_of_service: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="Home">Home</MenuItem>
                      <MenuItem value="Facility">Facility</MenuItem>
                      <MenuItem value="Office">Office</MenuItem>
                      <MenuItem value="Hospital">Hospital</MenuItem>
                      <MenuItem value="Nursing Home">Nursing Home</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {order.place_of_service}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Scheduled Date
                </Typography>
                {editingSection === "order" ? (
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={formatDateForInput(editedOrder.scheduled_date)}
                    onChange={(e) =>
                      setEditedOrder({
                        ...editedOrder,
                        scheduled_date: e.target.value,
                      })
                    }
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {order.scheduled_date
                      ? formatDateForDisplay(order.scheduled_date)
                      : "Not scheduled"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Scheduled Time (From)
                </Typography>
                {editingSection === "order" ? (
                  <TextField
                    type="time"
                    fullWidth
                    size="small"
                    value={editedOrder.scheduled_time || ""}
                    onChange={(e) =>
                      setEditedOrder({
                        ...editedOrder,
                        scheduled_time: e.target.value,
                      })
                    }
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {order.scheduled_time || "Not specified"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Scheduled Time (To)
                </Typography>
                {editingSection === "order" ? (
                  <TextField
                    type="time"
                    fullWidth
                    size="small"
                    value={editedOrder.scheduled_time_end || ""}
                    onChange={(e) =>
                      setEditedOrder({
                        ...editedOrder,
                        scheduled_time_end: e.target.value,
                      })
                    }
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {order.scheduled_time_end || "Not specified"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Ordering Health Company
                </Typography>
                {editingSection === "order" ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editedOrder.ordering_health_company || ""}
                    onChange={(e) =>
                      setEditedOrder({
                        ...editedOrder,
                        ordering_health_company: e.target.value,
                      })
                    }
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {order.ordering_health_company || "N/A"}
                  </Typography>
                )}
              </Grid>

              {/* Medical Professional Section */}
              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Medical Professional
                </Typography>
                {editingSection === "order" ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Medical Professional</InputLabel>
                    <Select
                      value={editedOrder.medical_professional_id || ""}
                      onChange={(e) =>
                        setEditedOrder({
                          ...editedOrder,
                          medical_professional_id: e.target.value,
                        })
                      }
                      label="Medical Professional"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {medicalProfessionals.map((mp) => (
                        <MenuItem key={mp.id} value={mp.id}>
                          {mp.user?.full_name || mp.id} ({mp.mp_category})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {order.medical_professional
                      ? order.medical_professional.user?.full_name ||
                        `MP-${order.medical_professional.id}`
                      : "Not assigned"}
                  </Typography>
                )}
              </Grid>

              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Special Notes
                </Typography>
                {editingSection === "order" ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={editedOrder.special_notes || ""}
                    onChange={(e) =>
                      setEditedOrder({
                        ...editedOrder,
                        special_notes: e.target.value,
                      })
                    }
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {order.special_notes || "No special notes"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* FIXED: Diagnoses and Exams with proper editing */}
          <Grid container spacing={isMobile ? 1.5 : 3}>
            <Grid  size={{ xs: 12, md: 4 }}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6" color="primary">
                      Diagnoses
                    </Typography>
                    {canEdit() && editingSection === "order" && (
                      <Typography variant="caption" color="text.secondary">
                        Editing enabled
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {editingSection === "order" ? (
                    <Autocomplete
                      multiple
                      options={diagnoses}
                      getOptionLabel={(option) =>
                        `${option.code} - ${option.description}`
                      }
                      value={editedOrder.selected_diagnoses || []}
                      onChange={handleDiagnosisEditChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Diagnoses"
                          size="small"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option.id}
                            label={`${option.code} - ${option.description}`}
                            {...getTagProps({ index })}
                            color={index === 0 ? "primary" : "default"}
                            size="small"
                          />
                        ))
                      }
                      isOptionEqualToValue={(option, value) => {
                        return (
                          option.id === value.id ||
                          option.id === value.diagnosis_id
                        );
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body2">
                              <strong>{option.code}</strong> -{" "}
                              {option.description}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  ) : (
                    <>
                      {order.diagnoses && order.diagnoses.length > 0 ? (
                        <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                          {order.diagnoses.map((diagnosisItem, index) => {
                            const { id, code, description, isPrimary } =
                              getDiagnosisDisplay(diagnosisItem);

                            return (
                              <Box
                                component="li"
                                key={id || index}
                                sx={{ mb: 1 }}
                              >
                                <Typography variant="body2">
                                  <strong>{code}</strong>: {description}
                                  {isPrimary && (
                                    <Chip
                                      label="Primary"
                                      color="primary"
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No diagnoses recorded
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid  size={{ xs: 12, md: 4 }}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6" color="primary">
                      Exams
                    </Typography>
                    {canEdit() && editingSection === "order" && (
                      <Button
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setShowCustomExamDialog(true)}
                        size="small"
                        disabled={saving}
                      >
                        Add Custom
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {editingSection === "order" ? (
                    <Autocomplete
                      multiple
                      options={examTypes}
                      getOptionLabel={(option) => option.name}
                      value={editedOrder.selected_exams || []}
                      onChange={handleExamEditChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Exams"
                          size="small"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body1">
                              {option.name}
                            </Typography>
                            {option.description && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option.id}
                            label={option.name}
                            {...getTagProps({ index })}
                            color="primary"
                            size="small"
                          />
                        ))
                      }
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value.id;
                      }}
                    />
                  ) : (
                    <>
                      {order.exams && order.exams.length > 0 ? (
                        <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                          {order.exams.map((examItem, index) => {
                            const { id, name, description, notes } =
                              getExamDisplay(examItem);

                            return (
                              <Box
                                component="li"
                                key={id || index}
                                sx={{ mb: 1 }}
                              >
                                {editingExam === id ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <TextField
                                      size="small"
                                      value={editExamName}
                                      onChange={(e) =>
                                        setEditExamName(e.target.value)
                                      }
                                      onKeyPress={(e) => {
                                        if (e.key === "Enter") {
                                          handleEditExamName(id, editExamName);
                                        }
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleEditExamName(id, editExamName)
                                      }
                                      disabled={saving}
                                    >
                                      <SaveIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => setEditingExam(null)}
                                    >
                                      <CancelEditIcon />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Box>
                                      <Typography variant="body2">
                                        <strong>{name}</strong>
                                        {description && `: ${description}`}
                                        {notes && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                              display: "block",
                                              fontStyle: "italic",
                                            }}
                                          >
                                            Notes: {notes}
                                          </Typography>
                                        )}
                                      </Typography>
                                    </Box>
                                    {canEdit() && (
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setEditingExam(id);
                                          setEditExamName(name);
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No exams recorded
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid  size={{ xs: 12, md: 4 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <AttachFileIcon color="primary" />
                      <Typography variant="h6" color="primary">
                        Attached Documents
                      </Typography>
                    </Box>

                    {canEdit() && !loadingFiles && (
                      <>
                        <input
                          accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.doc,.docx,.xls,.xlsx,.txt,.rtf"
                          style={{ display: "none" }}
                          id="file-upload-detail"
                          multiple
                          type="file"
                          onChange={handleFileUpload}
                          disabled={uploadingFile}
                        />
                        <label htmlFor="file-upload-detail">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={
                              uploadingFile ? (
                                <CircularProgress size={20} />
                              ) : (
                                <CloudUploadIcon />
                              )
                            }
                            disabled={uploadingFile}
                            size="small"
                          >
                            {uploadingFile ? "Uploading..." : "Upload File"}
                          </Button>
                        </label>
                      </>
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {loadingFiles ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress />
                    </Box>
                  ) : orderFiles.length === 0 ? (
                    <Alert severity="info">
                      <Typography variant="body2">
                        No documents attached to this order yet.
                        {canEdit() && " Click 'Upload File' to add documents."}
                      </Typography>
                    </Alert>
                  ) : (
                    <Box>
                      {orderFiles.map((file) => (
                        <Card
                          key={file.id}
                          variant="outlined"
                          sx={{ mb: 2, p: 2 }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={2}
                              flex={1}
                            >
                              {getFileIcon(file.mime_type)}
                              <Box flex={1}>
                                <Typography variant="body1" fontWeight={500}>
                                  {file.file_name}
                                </Typography>
                                <Box display="flex" gap={2} mt={0.5}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {formatFileSize(file.file_size)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {new Date(file.created_at).toLocaleString()}
                                  </Typography>
                                  {file.uploaded_by && (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      Uploaded by:{" "}
                                      {file.uploaded_by.full_name || "Unknown"}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>

                            <Box display="flex" gap={1}>
                              {canPreviewFile(file.mime_type) && (
                                <Tooltip title="Preview file">
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleViewFile(file)}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <Tooltip title="Download file">
                                <IconButton
                                  color="primary"
                                  onClick={() =>
                                    handleDownloadFile(file.id, file.file_name)
                                  }
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Tooltip>

                              {canEdit() && (
                                <Tooltip title="Delete file">
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteFileClick(file)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  )}

                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      <strong>Security Notice:</strong> All files are encrypted
                      and stored securely in compliance with HIPAA regulations.
                      Access is logged for audit purposes.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Grid>

            {/* Delete File Confirmation Dialog */}
            <Dialog
              open={deleteFileDialogOpen}
              onClose={() => setDeleteFileDialogOpen(false)}
            >
              <DialogTitle>Delete File</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete "{fileToDelete?.file_name}"?
                  This action cannot be undone and the file will be permanently
                  removed.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteFileDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteFileConfirm}
                  color="error"
                  variant="contained"
                >
                  Delete File
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>

        <Grid  size={{ xs: 12, md: 4 }}>
          {/* Patient Information */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" color="primary">
                  Patient Information
                </Typography>
                {shouldShowEditButton("patient") &&
                  editingSection !== "patient" && (
                    <Tooltip title={getEditButtonTooltip()}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick("patient")}
                        size="small"
                        color="primary"
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  )}
                {editingSection === "patient" && (
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveSection}
                      disabled={saving}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelEditIcon />}
                      onClick={handleCancelEdit}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {order.patient ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  {editingSection === "patient" ? (
                    <Box display="flex" gap={1} mb={2}>
                      <TextField
                        size="small"
                        label="First Name"
                        value={editedPatient.first_name || ""}
                        onChange={(e) =>
                          setEditedPatient({
                            ...editedPatient,
                            first_name: e.target.value,
                          })
                        }
                      />
                      <TextField
                        size="small"
                        label="Last Name"
                        value={editedPatient.last_name || ""}
                        onChange={(e) =>
                          setEditedPatient({
                            ...editedPatient,
                            last_name: e.target.value,
                          })
                        }
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.patient.first_name} {order.patient.last_name}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Date of Birth
                  </Typography>
                  {editingSection === "patient" ? (
                    <TextField
                      type="date"
                      fullWidth
                      size="small"
                      value={formatDateForInput(editedPatient.date_of_birth)}
                      onChange={(e) =>
                        setEditedPatient({
                          ...editedPatient,
                          date_of_birth: e.target.value,
                        })
                      }
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {formatDateForDisplay(order.patient.date_of_birth)}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Gender
                  </Typography>
                  {editingSection === "patient" ? (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <Select
                        value={editedPatient.gender || ""}
                        onChange={(e) =>
                          setEditedPatient({
                            ...editedPatient,
                            gender: e.target.value,
                          })
                        }
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.patient.gender || "N/A"}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Address
                  </Typography>
                  {editingSection === "patient" ? (
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Address"
                        value={editedPatient.address || ""}
                        onChange={(e) =>
                          setEditedPatient({
                            ...editedPatient,
                            address: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Address Notes"
                        value={editedPatient.address_notes || ""}
                        onChange={(e) =>
                          setEditedPatient({
                            ...editedPatient,
                            address_notes: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <Box display="flex" gap={1} mb={2}>
                        <TextField
                          size="small"
                          label="City"
                          value={editedPatient.city || ""}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              city: e.target.value,
                            })
                          }
                        />
                        <TextField
                          size="small"
                          label="State"
                          value={editedPatient.state || ""}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              state: e.target.value,
                            })
                          }
                        />
                        <TextField
                          size="small"
                          label="ZIP"
                          value={editedPatient.zip_code || ""}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              zip_code: e.target.value,
                            })
                          }
                        />
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body1" gutterBottom>
                        {order.patient.address}
                        {order.patient.city && `, ${order.patient.city}`}
                        {order.patient.state && `, ${order.patient.state}`}
                        {order.patient.zip_code && ` ${order.patient.zip_code}`}
                      </Typography>
                      {order.patient.address_notes &&
                        order.patient.address_notes.trim() !== "" &&
                        order.patient.address_notes !== "null" &&
                        order.patient.address_notes !== "undefined" && (
                          <>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              Address Notes
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              {order.patient.address_notes}
                            </Typography>
                          </>
                        )}
                    </>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Phone Number
                  </Typography>
                  {editingSection === "patient" ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editedPatient.phone_number || ""}
                      onChange={(e) =>
                        setEditedPatient({
                          ...editedPatient,
                          phone_number: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.patient.phone_number || "N/A"}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Patient information not available
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Physician Information */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" color="primary">
                  Physician Information
                </Typography>
                {shouldShowEditButton("physician") &&
                  editingSection !== "physician" && (
                    <Tooltip title={getEditButtonTooltip()}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick("physician")}
                        size="small"
                        color="primary"
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  )}
                {editingSection === "physician" && (
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveSection}
                      disabled={saving}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelEditIcon />}
                      onClick={handleCancelEdit}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {order.ordering_physician ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  {editingSection === "physician" ? (
                    <Box display="flex" gap={1} mb={2}>
                      <TextField
                        size="small"
                        label="First Name"
                        value={editedPhysician.first_name || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            first_name: e.target.value,
                          })
                        }
                      />
                      <TextField
                        size="small"
                        label="Last Name"
                        value={editedPhysician.last_name || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            last_name: e.target.value,
                          })
                        }
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      Dr. {order.ordering_physician.first_name}{" "}
                      {order.ordering_physician.last_name}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    NPI
                  </Typography>
                  {editingSection === "physician" ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="NPI Number"
                      value={editedPhysician.npi || ""}
                      onChange={(e) =>
                        setEditedPhysician({
                          ...editedPhysician,
                          npi: e.target.value,
                        })
                      }
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.ordering_physician.npi || "N/A"}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Specialty
                  </Typography>
                  {editingSection === "physician" ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Specialty"
                      value={editedPhysician.specialty || ""}
                      onChange={(e) =>
                        setEditedPhysician({
                          ...editedPhysician,
                          specialty: e.target.value,
                        })
                      }
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.ordering_physician.specialty || "N/A"}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Facility
                  </Typography>
                  {editingSection === "physician" ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Facility Name"
                      value={editedPhysician.facility_name || ""}
                      onChange={(e) =>
                        setEditedPhysician({
                          ...editedPhysician,
                          facility_name: e.target.value,
                        })
                      }
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.ordering_physician.facility_name || "N/A"}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  {editingSection === "physician" ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone Number"
                      value={editedPhysician.phone_number || ""}
                      onChange={(e) =>
                        setEditedPhysician({
                          ...editedPhysician,
                          phone_number: e.target.value,
                        })
                      }
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.ordering_physician.phone_number || "N/A"}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  {editingSection === "physician" ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={editedPhysician.email || ""}
                      onChange={(e) =>
                        setEditedPhysician({
                          ...editedPhysician,
                          email: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      {order.ordering_physician.email || "N/A"}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    gutterBottom
                  >
                    No physician assigned
                  </Typography>
                  {canEdit() && editingSection === "physician" && (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Add New Physician
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="First Name"
                        value={editedPhysician.first_name || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            first_name: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Last Name"
                        value={editedPhysician.last_name || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            last_name: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="NPI Number"
                        value={editedPhysician.npi || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            npi: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Specialty"
                        value={editedPhysician.specialty || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            specialty: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Facility Name"
                        value={editedPhysician.facility_name || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            facility_name: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone Number"
                        value={editedPhysician.phone_number || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            phone_number: e.target.value,
                          })
                        }
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        type="email"
                        value={editedPhysician.email || ""}
                        onChange={(e) =>
                          setEditedPhysician({
                            ...editedPhysician,
                            email: e.target.value,
                          })
                        }
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Route Information */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Route Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {order.route_group ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Route Group
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {order.route_group.name}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Scheduled Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDateForDisplay(order.route_group.scheduled_date)}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Box gutterBottom>
                    {getStatusChip(order.route_group.status)}
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/routes/${order.route_group.id}`)}
                    sx={{ mt: 1 }}
                  >
                    View Route Details
                  </Button>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    gutterBottom
                    color="text.secondary"
                  >
                    This order is not assigned to any route.
                  </Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate("/routes/optimize")}
                    sx={{ mt: 1 }}
                  >
                    Optimize Routes
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ENHANCED: Assignment/Reassignment Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {order?.medical_professional_id
            ? "Reassign Medical Professional"
            : "Assign Medical Professional"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {order?.medical_professional_id
              ? "Select a different medical professional for this order:"
              : "Please select a medical professional to assign this order to:"}
          </DialogContentText>

          {order?.medical_professional_id && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Currently assigned to:</strong>{" "}
                {order.medical_professional?.user?.full_name ||
                  `MP-${order.medical_professional_id}`}
              </Typography>
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Medical Professional</InputLabel>
            <Select
              value={selectedMedicalProfessional}
              onChange={(e) => setSelectedMedicalProfessional(e.target.value)}
              label="Medical Professional"
            >
              <MenuItem value="">
                <em>None (Unassign)</em>
              </MenuItem>
              {medicalProfessionals.map((mp) => (
                <MenuItem key={mp.id} value={mp.id}>
                  <Box>
                    <Typography variant="body1">
                      {mp.user?.full_name || mp.id} ({mp.mp_category})
                    </Typography>
                    {mp.user?.email && (
                      <Typography variant="caption" color="text.secondary">
                        {mp.user.email}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignOrder}
            color="primary"
            variant="contained"
            disabled={assigning}
          >
            {assigning
              ? "Processing..."
              : order?.medical_professional_id
              ? "Reassign"
              : "Assign Order"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          {dialogAction.status === "cancelled"
            ? "Cancel Order"
            : "Update Order Status"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction.status === "cancelled"
              ? "Are you sure you want to cancel this order? This action cannot be undone."
              : `Are you sure you want to change the status to "${dialogAction.status.replace(
                  "_",
                  " "
                )}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            No, Keep Current Status
          </Button>
          <Button
            onClick={handleDialogConfirm}
            color={dialogAction.status === "cancelled" ? "error" : "primary"}
            variant="contained"
          >
            Yes, Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADDED: Custom Exam Dialog */}
      <Dialog
        open={showCustomExamDialog}
        onClose={() => !saving && setShowCustomExamDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Custom Exam</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Exam Name"
                required
                value={newCustomExam.name}
                onChange={(e) =>
                  setNewCustomExam({ ...newCustomExam, name: e.target.value })
                }
                disabled={saving}
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={newCustomExam.description}
                onChange={(e) =>
                  setNewCustomExam({
                    ...newCustomExam,
                    description: e.target.value,
                  })
                }
                disabled={saving}
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={newCustomExam.duration || ""}
                onChange={(e) =>
                  setNewCustomExam({
                    ...newCustomExam,
                    duration: e.target.value
                      ? parseInt(e.target.value, 10)
                      : null,
                  })
                }
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCustomExamDialog(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCustomExam}
            variant="contained"
            disabled={!newCustomExam.name?.trim() || saving}
            startIcon={saving ? null : <AddIcon />}
          >
            {saving ? "Adding..." : "Add Exam"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FILE PREVIEW DIALOG */}
      <Dialog
        open={previewDialog}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Transition : undefined}
        PaperProps={{
          sx: {
            height: isMobile ? "100%" : "90vh",
            m: isMobile ? 0 : 2,
          },
        }}
      >
        {isMobile ? (
          // Mobile: Full screen with app bar
          <>
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={handleClosePreview}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
                <Typography
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                  component="div"
                  noWrap
                >
                  {currentFile?.file_name}
                </Typography>
                <IconButton
                  color="inherit"
                  onClick={() =>
                    handleDownloadFile(currentFile?.id, currentFile?.file_name)
                  }
                >
                  <DownloadIcon />
                </IconButton>
              </Toolbar>
            </AppBar>

            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                backgroundColor: "#000",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {loadingPreview ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                >
                  <CircularProgress size={60} sx={{ color: "white" }} />
                  <Typography color="white">Loading preview...</Typography>
                </Box>
              ) : previewError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {previewError}
                </Alert>
              ) : fileContent && currentFile ? (
                <>
                  {currentFile.mime_type?.includes("image") ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "auto",
                        p: 2,
                      }}
                    >
                      <img
                        src={fileContent}
                        alt={currentFile.file_name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          transform: `scale(${zoomLevel})`,
                          transition: "transform 0.2s",
                        }}
                      />
                    </Box>
                  ) : currentFile.mime_type?.includes("pdf") ? (
                    <iframe
                      src={fileContent}
                      title={currentFile.file_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                    />
                  ) : null}

                  {/* Zoom Controls for Images */}
                  {currentFile.mime_type?.includes("image") && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: 1,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        borderRadius: 3,
                        p: 1,
                      }}
                    >
                      <Fab
                        size="small"
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 0.5}
                        sx={{ backgroundColor: "white" }}
                      >
                        <ZoomOutIcon />
                      </Fab>
                      <Chip
                        label={`${Math.round(zoomLevel * 100)}%`}
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          minWidth: 70,
                        }}
                      />
                      <Fab
                        size="small"
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 3}
                        sx={{ backgroundColor: "white" }}
                      >
                        <ZoomInIcon />
                      </Fab>
                    </Box>
                  )}
                </>
              ) : null}
            </Box>
          </>
        ) : (
          // Desktop: Regular dialog
          <>
            <DialogTitle>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {currentFile && getFileIcon(currentFile.mime_type)}
                  <Typography variant="h6" noWrap sx={{ maxWidth: 400 }}>
                    {currentFile?.file_name}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton
                    onClick={() =>
                      handleDownloadFile(
                        currentFile?.id,
                        currentFile?.file_name
                      )
                    }
                    color="primary"
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton onClick={handleClosePreview}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent
              sx={{
                p: 0,
                backgroundColor: "#000",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {loadingPreview ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                  py={8}
                >
                  <CircularProgress size={60} sx={{ color: "white" }} />
                  <Typography color="white">Loading preview...</Typography>
                </Box>
              ) : previewError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {previewError}
                </Alert>
              ) : fileContent && currentFile ? (
                <>
                  {currentFile.mime_type?.includes("image") ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "auto",
                        p: 2,
                      }}
                    >
                      <img
                        src={fileContent}
                        alt={currentFile.file_name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          transform: `scale(${zoomLevel})`,
                          transition: "transform 0.2s",
                        }}
                      />
                    </Box>
                  ) : currentFile.mime_type?.includes("pdf") ? (
                    <iframe
                      src={fileContent}
                      title={currentFile.file_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                    />
                  ) : null}

                  {/* Zoom Controls for Images */}
                  {currentFile.mime_type?.includes("image") && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: 1,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        borderRadius: 3,
                        p: 1,
                      }}
                    >
                      <IconButton
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 0.5}
                        sx={{ color: "white" }}
                      >
                        <ZoomOutIcon />
                      </IconButton>
                      <Chip
                        label={`${Math.round(zoomLevel * 100)}%`}
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          minWidth: 70,
                        }}
                      />
                      <IconButton
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 3}
                        sx={{ color: "white" }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  )}
                </>
              ) : null}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OrderDetail;